import { DateTime } from 'luxon'
import { CalendarBean, Day } from './types'

/**
 * A service class for generating and managing calendar data.
 */
export class CalendarService {
  /**
   * The calendar data containing weeks and days.
   * @private
   * @type {CalendarBean}
   */
  private calendar: CalendarBean = { weeks: [{ days: [] }] }

  /**
   * The list of days in the current month.
   * @private
   * @type {Day[]}
   */
  private month: Day[] = []

  /**
   * Generates the days for the current month, including adjacent days from the previous and next months.
   * @param {Date} selectedMonth - The selected month for which to generate the days.
   */
  getCurrentMonth(selectedMonth: Date) {
    this.month = []

    // Start of the selected month
    let startMonth = DateTime.fromJSDate(selectedMonth).startOf('month')
    const prevMonth = DateTime.fromJSDate(selectedMonth).startOf('month').minus({ days: 1 })
    let fored = false
    const endMonth = DateTime.fromJSDate(selectedMonth).endOf('month')

    // Adjust startMonth to the beginning of the week
    if (startMonth.weekday === 0) {
      startMonth = startMonth.minus({ days: 6 })
      fored = true
    } else if (startMonth.weekday > 1) {
      startMonth = startMonth.plus({ days: 1 - startMonth.weekday })
      fored = true
    }

    // Add days from the previous month if needed
    if (fored) {
      for (let i = startMonth.day; i <= prevMonth.day; i++) {
        this.month.push({
          date: startMonth.toJSDate(),
          day: startMonth.weekday,
          otherMonth: true,
        })
        startMonth = startMonth.plus({ days: 1 })
      }
      startMonth = DateTime.fromJSDate(selectedMonth).startOf('month')
    }

    // Add days for the current month
    for (let i = startMonth.day; i <= endMonth.day; i++) {
      this.month.push({
        date: startMonth.toJSDate(),
        day: startMonth.weekday,
      })
      startMonth = startMonth.plus({ days: 1 })
    }

    // Add days from the next month to complete the last week
    if (endMonth.weekday === 6) {
      this.month.push({
        date: startMonth.toJSDate(),
        day: startMonth.weekday,
        otherMonth: true,
      })
    } else if (endMonth.weekday < 6 && endMonth.weekday !== 0) {
      for (let i = endMonth.weekday; i < 7; i++) {
        this.month.push({
          date: startMonth.toJSDate(),
          day: startMonth.weekday,
          otherMonth: true,
        })
        startMonth = startMonth.plus({ days: 1 })
      }
    }
  }

  /**
   * Generates a calendar structure for the given month, organized by weeks and days.
   * @param {Date} selectedMonth - The selected month for which to generate the calendar.
   * @returns {CalendarBean} The calendar data for the selected month.
   */
  getMonthCalendar(selectedMonth: Date): CalendarBean {
    this.calendar = { weeks: [{ days: [] }] }
    this.getCurrentMonth(selectedMonth)
    let i = 0
    let week = 0
    while (i < this.month.length) {
      for (let j = 0; j < 7; j++) {
        if (this.month[i]) {
          const luxonDate = DateTime.fromJSDate(this.month[i].date)
          this.calendar.weeks[week].days.push({
            date: this.month[i].date,
            day: this.month[i].day,
            monthDay: Number(luxonDate.toFormat('dd')),
            otherMonth: this.month[i].otherMonth,
            // Uncomment the line below to include daysFromToday
            // daysFromToday: Number(luxonDate.diff(DateTime.local().startOf('day'), 'days').days.toFixed(0)),
          })

          if (i <= this.month.length) {
            i++
          }
        }
      }
      if (i < this.month.length) {
        this.calendar.weeks.push({ days: [] })
        week++
      }
    }
    return this.calendar
  }
}
